import React, { useState } from 'react';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const ProjectCard = ({ project, onViewDetails, isListView = false }) => {
  const [isImageLoaded, setIsImageLoaded] = useState(false);

  const handleImageLoad = () => {
    setIsImageLoaded(true);
  };

  const getTechniqueColor = (technique) => {
    const colors = {
      'digital': 'bg-blue-100 text-blue-800',
      'screen': 'bg-green-100 text-green-800',
      'sublimation': 'bg-purple-100 text-purple-800',
      'heat-transfer': 'bg-orange-100 text-orange-800',
      'block': 'bg-amber-100 text-amber-800',
      'embroidery': 'bg-pink-100 text-pink-800'
    };
    return colors?.[technique] || 'bg-gray-100 text-gray-800';
  };

  if (isListView) {
    return (
      <div className="bg-card border border-border rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-300">
        <div className="flex flex-col md:flex-row">
          {/* Image Section */}
          <div className="relative md:w-80 h-48 md:h-auto overflow-hidden bg-muted">
            {!isImageLoaded && (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            )}
            <Image
              src={project?.image}
              alt={project?.title}
              className={`w-full h-full object-cover transition-opacity duration-300 ${
                isImageLoaded ? 'opacity-100' : 'opacity-0'
              }`}
              onLoad={handleImageLoad}
            />
            <div className="absolute top-3 left-3">
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getTechniqueColor(project?.technique)}`}>
                {project?.technique?.charAt(0)?.toUpperCase() + project?.technique?.slice(1)}
              </span>
            </div>
          </div>

          {/* Content Section */}
          <div className="flex-1 p-6">
            <div className="flex items-start justify-between mb-3">
              <div>
                <h3 className="text-lg font-semibold text-text-primary mb-1">{project?.title}</h3>
                <p className="text-sm text-text-secondary">{project?.client}</p>
              </div>
              <div className="flex items-center gap-1 text-golden">
                <Icon name="Star" size={14} fill="currentColor" />
                <span className="text-sm font-medium">{project?.rating}</span>
              </div>
            </div>

            <p className="text-text-secondary text-sm mb-4 line-clamp-2">{project?.description}</p>

            <div className="flex flex-wrap gap-2 mb-4">
              <span className="px-2 py-1 bg-muted text-text-secondary text-xs rounded">
                {project?.material}
              </span>
              <span className="px-2 py-1 bg-muted text-text-secondary text-xs rounded">
                {project?.industry}
              </span>
              <span className="px-2 py-1 bg-muted text-text-secondary text-xs rounded">
                {project?.quantity} pieces
              </span>
            </div>

            <div className="flex items-center justify-between">
              <div className="text-sm text-text-secondary">
                Completed: {project?.completedDate}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onViewDetails(project)}
                iconName="Eye"
                iconPosition="left"
                iconSize={14}
              >
                View Details
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-all duration-300 group">
      {/* Image Section */}
      <div className="relative h-48 sm:h-56 md:h-64 lg:h-72 overflow-hidden bg-muted">
        {!isImageLoaded && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        )}
        <Image
          src={project?.image}
          alt={project?.title}
          className={`w-full h-full object-cover transition-all duration-300 group-hover:scale-105 ${
            isImageLoaded ? 'opacity-100' : 'opacity-0'
          }`}
          onLoad={handleImageLoad}
        />
        
        {/* Overlay */}
        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-300"></div>
        
        {/* Technique Badge */}
        <div className="absolute top-2 sm:top-3 left-2 sm:left-3">
          <span className={`px-2 py-1 rounded-full text-[10px] sm:text-xs font-medium ${getTechniqueColor(project?.technique)}`}>
            {project?.technique?.charAt(0)?.toUpperCase() + project?.technique?.slice(1)}
          </span>
        </div>

        {/* Rating */}
        <div className="absolute top-2 sm:top-3 right-2 sm:right-3 flex items-center gap-1 bg-white bg-opacity-90 px-2 py-1 rounded-full">
          <Icon name="Star" size={10} fill="currentColor" className="text-golden sm:w-3 sm:h-3" />
          <span className="text-[10px] sm:text-xs font-medium">{project?.rating}</span>
        </div>

        {/* View Details Button - Appears on Hover */}
        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <Button
            variant="default"
            size="sm"
            onClick={() => onViewDetails(project)}
            iconName="Eye"
            iconPosition="left"
            iconSize={14}
            className="bg-white text-primary hover:bg-gray-50 text-xs sm:text-sm font-semibold"
          >
            View Details
          </Button>
        </div>
      </div>
      {/* Content Section */}
      <div className="p-3 sm:p-4 lg:p-5">
        <div className="flex items-start justify-between mb-1.5 sm:mb-2">
          <div>
            <h3 className="text-sm sm:text-base lg:text-lg font-semibold text-text-primary mb-0.5 sm:mb-1 line-clamp-1">{project?.title}</h3>
            <p className="text-xs sm:text-sm text-text-secondary">{project?.client}</p>
          </div>
        </div>

        <p className="text-text-secondary text-xs sm:text-sm mb-2 sm:mb-3 line-clamp-2 leading-relaxed">{project?.description}</p>

        <div className="flex flex-wrap gap-1 sm:gap-1.5 mb-2 sm:mb-3">
          <span className="px-1.5 sm:px-2 py-0.5 sm:py-1 bg-muted text-text-secondary text-[10px] sm:text-xs rounded">
            {project?.material}
          </span>
          <span className="px-1.5 sm:px-2 py-0.5 sm:py-1 bg-muted text-text-secondary text-[10px] sm:text-xs rounded">
            {project?.industry}
          </span>
        </div>

        <div className="flex items-center justify-between text-xs sm:text-sm">
          <span className="text-text-secondary">{project?.quantity} pieces</span>
          <span className="text-text-secondary">{project?.completedDate}</span>
        </div>
      </div>
    </div>
  );
};

export default ProjectCard;